import { Ref, ref } from "vue";
import { AxiosError, AxiosResponse } from "axios";
import { useI18n } from "@/i18n";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { getPrivacyPolicy } from "@/apps/merchant3/services/privacy-policy.service";
import { getTermsAndConditions } from "@/apps/merchant3/services/termsAndConditions.service";
import logEvent from "@/apps/merchant3/services/event-logs.service";

type ComposableResult = {
  privacyPolicyContent: Ref<string>;
  termsContent: Ref<string>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  isAllContentLoaded: Ref<boolean>;
};

export function useTermsAndPrivacyContent(
  locale: string,
  cartTag?: string
): ComposableResult {
  const { t } = useI18n();
  const privacyPolicyContent = ref("");
  const termsContent = ref("");
  const loading = ref(false);
  const error = ref<string | null>(null);
  const isAllContentLoaded = ref(false);

  const fetchPrivacyPolicy = async () => {
    return getPrivacyPolicy(locale, cartTag).then((response: AxiosResponse) => {
      logEvent(EventLogs.Events.PRIVACY_POLICY_LOADED);
      privacyPolicyContent.value = response.data?.document;
      loading.value = false;
    });
  };

  const fetchTermsAndConditions = async () => {
    return getTermsAndConditions(locale, cartTag).then(
      (response: AxiosResponse) => {
        logEvent(EventLogs.Events.TERMS_AND_CONDITIONS_LOADED);
        termsContent.value = response.data?.document;
        loading.value = false;
      }
    );
  };

  loading.value = true;
  Promise.all([fetchPrivacyPolicy(), fetchTermsAndConditions()])
    .then(() => {
      isAllContentLoaded.value = true;
    })
    .catch((e: AxiosError) => {
      error.value = getAPIErrorMessage(e.response?.data, t);
      isAllContentLoaded.value = true;
    })
    .finally(() => {
      loading.value = false;
    });

  return {
    termsContent,
    privacyPolicyContent,
    loading,
    error,
    isAllContentLoaded,
  };
}
